import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "catalyst-ui-kit/dialog";
import DatePickerx from "features/datepicker/date-picker";
import {
  fetchProbationReviewByUser,
  submitProbationReviewDecision,
  updateProbationReviewEndDate,
  PROBATION_CRITERIA,
  PROBATION_NA_CRITERIA,
  ProbationCriterionKey,
  ProbationDecision,
  ProbationRating,
  ProbationReviewByUserResponse,
} from "services/probation-review.service";
import { formatDay } from "features/personnel/personnel-helpers";
import { vilniusTodayYmd } from "utils/vilnius-date";

const RATINGS_BASE: ProbationRating[] = ["meets", "partly", "not_meets"];
const RATING_LABEL_KEY: Record<ProbationRating, string> = {
  meets: "probation_rating_meets",
  partly: "probation_rating_partly",
  not_meets: "probation_rating_not_meets",
  na: "probation_rating_na",
};
const CRITERION_LABEL_KEY: Record<ProbationCriterionKey, string> = {
  quality: "probation_criterion_quality",
  workload: "probation_criterion_workload",
  independence: "probation_criterion_independence",
  discipline: "probation_criterion_discipline",
  teamwork: "probation_criterion_teamwork",
  trainings: "probation_criterion_trainings",
  targets: "probation_criterion_targets",
};

const COMMENT_MIN_LENGTH = 10;
const COMMENT_MAX_LENGTH = 4000;

const ERROR_MESSAGE_KEYS: Record<string, string> = {
  PROBATION_REVIEW_INVALID: "probation_review_invalid",
  PROBATION_REVIEW_FORBIDDEN: "probation_review_forbidden",
  PROBATION_REVIEW_CLOSED: "probation_review_closed",
  PROBATION_NOT_APPLICABLE: "probation_not_applicable",
};

function errorMessageKey(e: any): string {
  const code = e?.response?.data?.error;
  return (typeof code === "string" && ERROR_MESSAGE_KEYS[code]) || "error";
}

/**
 * PATCH .../end-date (§5.4) rejects a date before today with
 * `400 PROBATION_REVIEW_INVALID {field:'probationEndDate'}` — a clearer,
 * field-specific message beats the generic "check the review fields" one.
 */
function endDateErrorMessageKey(e: any): string {
  const data = e?.response?.data;
  if (data?.error === "PROBATION_REVIEW_INVALID" && data?.field === "probationEndDate") {
    return "probation_end_date_invalid";
  }
  return errorMessageKey(e);
}

/**
 * Today's Europe/Vilnius calendar date as a plain LOCAL Date — the shape
 * `disableDatesBefore` expects (date-picker.jsx reads getFullYear/Month/Date
 * off it directly, in the browser's own zone, not as a UTC instant).
 */
function vilniusTodayLocalDate(): Date {
  const [y, m, d] = vilniusTodayYmd().split("-").map(Number);
  return new Date(y, m - 1, d);
}

export interface ProbationReviewDialogProps {
  companyId: string;
  userId: string;
  open: boolean;
  onClose: () => void;
  /** After a successful decision or end-date save — reload the caller's list/summary. */
  onSaved?: () => void;
}

/**
 * The probation review card (docs/prd/probation-salary-review.md §7.6):
 * opened from the personnel card's «Bandomasis laikotarpis» tile and from
 * the «Užduotys» → «Bandomojo laikotarpio vertinimai» list. Fetches its
 * own data on open so it always shows the latest state regardless of caller.
 */
/**
 * UTC midnight of a YYYY-MM-DD day: how the backend stores a Vilnius calendar
 * day (vilniusDayAsUtcMidnight) and what the date picker hands out.
 */
function utcMidnightOf(day: string): number {
  const [year, month, date] = day.split("-").map(Number);
  return Date.UTC(year, month - 1, date);
}

/** A radio option. On a phone it is drawn as a 44 px chip (the whole chip is the tap target), with the chosen one highlighted. */
function RadioChip({
  name,
  label,
  checked,
  disabled,
  onChange,
}: {
  name: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-1.5 text-sm text-gray-700 max-sm:min-h-11 max-sm:rounded-lg max-sm:border max-sm:px-3 max-sm:py-2 ${
        checked ? "max-sm:border-blue-600 max-sm:bg-blue-50" : "max-sm:border-gray-300"
      }`}
    >
      <input type="radio" className="max-sm:size-5" name={name} checked={checked} onChange={onChange} disabled={disabled} />
      {label}
    </label>
  );
}

export default function ProbationReviewDialog({ companyId, userId, open, onClose, onSaved }: ProbationReviewDialogProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState<ProbationReviewByUserResponse | null>(null);
  const [ratings, setRatings] = useState<Partial<Record<ProbationCriterionKey, ProbationRating>>>({});
  const [decision, setDecision] = useState<ProbationDecision | null>(null);
  const [comment, setComment] = useState("");
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  // The end date being edited, as a calendar day (YYYY-MM-DD); the review may carry it as a
  // timestamp or a day string; it is sent back as that day's UTC midnight.
  const [endDateDraft, setEndDateDraft] = useState<string | null>(null);
  const [savingEndDate, setSavingEndDate] = useState(false);
  const [endDateError, setEndDateError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    setErrors({});
    setEndDateError(null);
    fetchProbationReviewByUser(companyId, userId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setRatings((res.review?.ratings as any) || {});
        setDecision(res.review?.status === "passed" || res.review?.status === "failed" ? res.review.status : null);
        setComment(res.review?.comment || "");
        setEndDateDraft(formatDay(res.review?.probationEndDate) || null);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, companyId, userId]);

  const review = data?.review || null;
  const can = data?.can || { submit: false, editEndDate: false };
  const readOnly = !can.submit;

  const naAllowed = (key: ProbationCriterionKey) => PROBATION_NA_CRITERIA.includes(key);
  const ratingOptions = (key: ProbationCriterionKey) => (naAllowed(key) ? [...RATINGS_BASE, "na" as const] : RATINGS_BASE);

  const setRating = (key: ProbationCriterionKey, value: ProbationRating) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: false }));
  };

  const validate = (): boolean => {
    const next: Record<string, boolean> = {};
    for (const key of PROBATION_CRITERIA) {
      const value = ratings[key];
      if (!value || (value === "na" && !naAllowed(key))) next[key] = true;
    }
    if (!decision) next.decision = true;
    const trimmed = comment.trim();
    if (decision === "failed" && trimmed.length < COMMENT_MIN_LENGTH) next.comment = true;
    if (trimmed.length > COMMENT_MAX_LENGTH) next.comment = true;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (readOnly || saving) return;
    // validate() also flags a missing decision, so its error is shown instead of Save doing nothing.
    if (!validate() || !decision) return;
    setSaving(true);
    try {
      const result = await submitProbationReviewDecision(companyId, userId, {
        decision,
        ratings,
        comment: comment.trim() || undefined,
      });
      toast.success(t("saved"));
      onSaved?.();
      setData(result);
      onClose();
    } catch (e: any) {
      toast.error(t(errorMessageKey(e)));
      if (e?.response?.status === 409 || e?.response?.status === 404) {
        // The row moved on under us (deadline passed / no longer applicable) — refresh instead of retrying blind.
        fetchProbationReviewByUser(companyId, userId).then(setData).catch(() => {});
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEndDate = async () => {
    if (!can.editEndDate || endDateDraft == null || savingEndDate) return;
    setSavingEndDate(true);
    setEndDateError(null);
    try {
      const result = await updateProbationReviewEndDate(companyId, userId, utcMidnightOf(endDateDraft));
      toast.success(t("saved"));
      setData(result);
      onSaved?.();
    } catch (e: any) {
      const key = endDateErrorMessageKey(e);
      toast.error(t(key));
      setEndDateError(key);
    } finally {
      setSavingEndDate(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? () => {} : onClose} size="lg" className="max-sm:[--gutter:1rem]">
      <DialogTitle>{t("probation_review_dialog_title")}</DialogTitle>
      <DialogBody>
        {loading ? (
          <p className="text-sm text-gray-500">{t("loading")}</p>
        ) : loadFailed || !review ? (
          <p className="text-sm text-red-600">{t(loadFailed ? "error" : "probation_not_applicable")}</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm md:grid-cols-3">
              <div>
                <p className="text-gray-500">{t("employee")}</p>
                <p className="font-medium">{review.employeeName}</p>
              </div>
              <div>
                <p className="text-gray-500">{t("position")}</p>
                <p>{review.positionLT || "—"}</p>
              </div>
              <div>
                <p className="text-gray-500">{t("hiring_date")}</p>
                <p>{formatDay(review.hiringDate)}</p>
              </div>
              <div>
                <p className="text-gray-500">{t("probation_end_date_field")}</p>
                <p>{formatDay(review.probationEndDate)}</p>
              </div>
              <div>
                <p className="text-gray-500">{t("probation_decision_deadline")}</p>
                <p>{formatDay(review.decisionDeadline)}</p>
              </div>
              <div>
                <p className="text-gray-500">{t("probation_notice_deadline")}</p>
                <p>{formatDay(review.noticeDeadline)}</p>
              </div>
            </div>

            {review.absenceDaysHint != null && (
              <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
                {t("probation_absence_hint", { days: review.absenceDaysHint })}
              </p>
            )}

            {readOnly && (
              <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">{t("probation_read_only_hint")}</p>
            )}

            <div className="flex flex-col gap-3">
              {PROBATION_CRITERIA.map((key) => (
                <div key={key}>
                  <p className="text-sm font-medium text-gray-800">{t(CRITERION_LABEL_KEY[key])}</p>
                  <div className="mt-1 flex flex-wrap gap-4 max-sm:gap-2">
                    {ratingOptions(key).map((option) => (
                      <RadioChip
                        key={option}
                        name={`probation-${key}`}
                        label={t(RATING_LABEL_KEY[option])}
                        checked={ratings[key] === option}
                        onChange={() => setRating(key, option)}
                        disabled={readOnly}
                      />
                    ))}
                  </div>
                  {errors[key] && <p className="mt-1 text-xs text-red-600">{t("field_is_required")}</p>}
                </div>
              ))}
            </div>

            <div>
              <p className="text-sm font-medium text-gray-800">{t("probation_decision_label")}</p>
              <div className="mt-1 flex flex-wrap gap-4 max-sm:gap-2">
                <RadioChip
                  name="probation-decision"
                  label={t("probation_decision_option_passed")}
                  checked={decision === "passed"}
                  onChange={() => {
                    setDecision("passed");
                    setErrors((prev) => ({ ...prev, decision: false }));
                  }}
                  disabled={readOnly}
                />
                <RadioChip
                  name="probation-decision"
                  label={t("probation_decision_option_failed")}
                  checked={decision === "failed"}
                  onChange={() => {
                    setDecision("failed");
                    setErrors((prev) => ({ ...prev, decision: false }));
                  }}
                  disabled={readOnly}
                />
              </div>
              {errors.decision && <p className="mt-1 text-xs text-red-600">{t("field_is_required")}</p>}
            </div>

            <div>
              <label htmlFor="probation-comment" className="mb-1 block text-sm font-medium text-gray-700">
                {t("probation_comment_label")}
                {decision === "failed" && <span className="ml-0.5 text-red-500">*</span>}
              </label>
              <textarea
                id="probation-comment"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 md:text-sm"
                rows={3}
                maxLength={COMMENT_MAX_LENGTH}
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  setErrors((prev) => ({ ...prev, comment: false }));
                }}
                disabled={readOnly}
              />
              {errors.comment && <p className="mt-1 text-xs text-red-600">{t("probation_comment_required_min")}</p>}
            </div>

            {can.editEndDate && (
              <div className="rounded-md border border-gray-200 p-3">
                <p className="mb-2 text-sm font-medium text-gray-800">{t("probation_end_date_label")}</p>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="sm:flex-1">
                    <DatePickerx
                      title={t("probation_end_date_label")}
                      selected={endDateDraft || ""}
                      onChange={(date: Date | null) => {
                        setEndDateDraft(date ? formatDay(date.getTime()) : null);
                        setEndDateError(null);
                      }}
                      isRequired
                      disableDatesAfter={null}
                      disableDatesBefore={vilniusTodayLocalDate() as any}
                    />
                  </div>
                  <button
                    type="button"
                    className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50 max-sm:min-h-11"
                    onClick={handleSaveEndDate}
                    disabled={savingEndDate || endDateDraft == null || endDateDraft === formatDay(review.probationEndDate)}
                  >
                    {savingEndDate ? "…" : t("save")}
                  </button>
                </div>
                {endDateError && <p className="mt-1 text-xs text-red-600">{t(endDateError)}</p>}
              </div>
            )}
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <button
          type="button"
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-sm:min-h-11"
          onClick={onClose}
          disabled={saving}
        >
          {t("close")}
        </button>
        {!readOnly && review && (
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 max-sm:min-h-11"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "…" : t("save")}
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
}
